/**
 * Ted #2 (#905) — check in a household's dogs together.
 *
 * Ted (HTHD, 09-04): "One Gingr feature that is very helpful is the ability
 * to check in multiple dogs belonging to the same client at once. For
 * example, with Hazel, Hank, and Skipper, we can select all three and check
 * them in together rather than processing each dog individually."
 *
 * Pure helpers shared by the three check-in surfaces — the dashboard's
 * ArrivalsDepartures widget, the Front Desk page, and (Ted's 09-15 update
 * on #905, plus the check-in half of Ted #26 / #987) the Bookings list —
 * so they group, batch and word failures identically (Principle 1: one
 * truth). No new server
 * route: the bulk action is N calls to the existing
 * POST /api/bookings/admin/:id/check-in, one per booking, and the failure of
 * one dog never stops the others.
 *
 * Grouping key is `customerId` ONLY. Two "J. Smith"s are not one household,
 * so rows whose payload carries no customer id (older server, or a booking
 * with no customer) are never grouped, even when the names match.
 */
import { ApiError, isArrivalUnverified, isDoNotAdmit } from '../../lib/api.js';
import type { DashboardArrival } from './dashboard-api.js';

/**
 * The only shape these helpers need from a row. Ted #2 follow-up (#905 /
 * #987): the Bookings page groups `AdminBooking` (snake_case, a different
 * payload from `/today`'s `DashboardArrival`), and a second copy of the
 * grouping/batching/failure-wording rules is a second truth waiting to
 * drift (Principle 1). Widening the constraint to this minimal interface
 * lets all three surfaces — Dashboard Arrivals, Front Desk and Bookings —
 * run the SAME code. `DashboardArrival` already satisfies it.
 */
export interface HouseholdRow {
  readonly bookingId: string;
  /** The owner. Absent/blank rows are never grouped — see segmentByHousehold. */
  readonly customerId?: string | null;
  readonly customerName: string;
  readonly dogNames: readonly string[];
}

export interface HouseholdSegment<T extends HouseholdRow = DashboardArrival> {
  /** The shared owner id, or null for a row that cannot be grouped. */
  customerId: string | null;
  customerName: string;
  rows: T[];
}

/**
 * Partition arrivals into first-seen-order segments: one per customer id
 * (all of that owner's rows, in list order) or one per ungrouped row. Rows
 * of the same household become adjacent — the same presentational move
 * OpenInvoices makes for the checkout cart (#813).
 */
export function segmentByHousehold<T extends HouseholdRow>(
  rows: ReadonlyArray<T>,
): HouseholdSegment<T>[] {
  const segments: HouseholdSegment<T>[] = [];
  const byCustomer = new Map<string, HouseholdSegment<T>>();
  for (const row of rows) {
    const id =
      typeof row.customerId === 'string' && row.customerId.trim().length > 0
        ? row.customerId
        : null;
    if (id === null) {
      segments.push({ customerId: null, customerName: row.customerName, rows: [row] });
      continue;
    }
    const existing = byCustomer.get(id);
    if (existing) {
      existing.rows.push(row);
      continue;
    }
    const segment: HouseholdSegment<T> = {
      customerId: id,
      customerName: row.customerName,
      rows: [row],
    };
    byCustomer.set(id, segment);
    segments.push(segment);
  }
  return segments;
}

/** "Hazel" · "Hazel & Hank" · "Hazel, Hank & Skipper". */
export function joinDogNames(names: ReadonlyArray<string>): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/** Every dog across the rows, in row order (a booking can carry >1 dog). */
export function householdDogNames(rows: ReadonlyArray<HouseholdRow>): string[] {
  return rows.flatMap((r) => [...r.dogNames]);
}

/**
 * Turn a per-dog check-in failure into the reason the desk reads on the
 * banner — the situation, never the technology (DESIGN-PHILOSOPHY
 * anti-patterns). The server's 400 for a booking that is not
 * confirmed/pending reads "Cannot transition booking from '<status>' to
 * 'checked_in'"; the status inside it is the only fact the desk needs.
 */
const TRANSITION_FROM_RE = /from '([a-z_]+)'/i;

export function describeCheckInFailure(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return 'booking not found — it may have been changed or removed';
    if (err.status === 403) return "you don't have permission to check dogs in";
    // Ted #28 (#1050) — reached only when a surface declined to ask (the
    // batch helper routes this code to `needsArrivalCheck` instead).
    if (isArrivalUnverified(err)) return 'arrives by transport — not yet confirmed here';
    // Ted 09-24 (lane LC) — Do Not Admit, the hard stop. The server's
    // sentence already names the dog and the reason; the batch rule is that
    // this dog lands in `failed` (named on the banner) while its siblings go
    // in — "Check in all" never sweeps past a hard stop.
    if (isDoNotAdmit(err)) return err.message.replace(/\.?\s*An owner or manager decides\.$/, '');
    if (err.status === 400) {
      const current = TRANSITION_FROM_RE.exec(err.message)?.[1];
      switch (current) {
        case 'checked_in':
          return 'already checked in';
        case 'checked_out':
        case 'completed':
          return 'already checked out';
        case 'cancelled':
          return 'this booking was cancelled';
        case 'no_show':
          return 'marked as a no-show';
        default:
          return err.message;
      }
    }
    return err.message;
  }
  // `fetch` rejects with a TypeError when the network is down.
  if (err instanceof TypeError) return "couldn't reach the server — check the connection and try again";
  return err instanceof Error && err.message.length > 0 ? err.message : 'something went wrong';
}

export interface HouseholdCheckInOutcome<T extends HouseholdRow = DashboardArrival> {
  succeeded: T[];
  failed: Array<{ row: T; reason: string }>;
  /**
   * Ted #28 (#1050) — THE BATCH RULE. A transported dog is never swept in by
   * "Check in all": the server refused it with `arrival_unverified`, and it
   * lands HERE, not in `failed`, so the surface asks about each one on its
   * own (Ted #26's "never removing staff control") while its walk-in
   * siblings are already checked in. Empty on an older server.
   */
  needsArrivalCheck: T[];
}

/**
 * Run the check-in for every row and settle ALL of them — one dog's refusal
 * (already in, cancelled, not found, network) never stops the siblings.
 * Successes are real server-side check-ins; the caller reconciles with ONE
 * refresh afterwards.
 */
export async function checkInHousehold<T extends HouseholdRow>(
  rows: ReadonlyArray<T>,
  run: (row: T) => Promise<unknown>,
): Promise<HouseholdCheckInOutcome<T>> {
  const results = await Promise.allSettled(rows.map((row) => run(row)));
  const outcome: HouseholdCheckInOutcome<T> = {
    succeeded: [],
    failed: [],
    needsArrivalCheck: [],
  };
  results.forEach((result, i) => {
    const row = rows[i]!;
    if (result.status === 'fulfilled') outcome.succeeded.push(row);
    else if (isArrivalUnverified(result.reason)) outcome.needsArrivalCheck.push(row);
    else outcome.failed.push({ row, reason: describeCheckInFailure(result.reason) });
  });
  return outcome;
}

/**
 * The two halves of what the desk is told after a household check-in, kept
 * apart so a surface can render them in the right TONE: `ok` is good news
 * and belongs in a status notice; `problem` is the refusal and belongs in
 * the error banner. #905/#987 review fix — a partial success used to put
 * "Checked in <dog>." inside red error chrome, which reads as a failure.
 * Either half is null when there is nothing of that kind to say.
 */
export function describeHouseholdCheckIn(
  outcome: HouseholdCheckInOutcome<HouseholdRow>,
): { ok: string | null; problem: string | null } {
  const ok =
    outcome.succeeded.length > 0
      ? `Checked in ${joinDogNames(householdDogNames(outcome.succeeded))}.`
      : null;
  const problem =
    outcome.failed.length > 0
      ? `Not checked in — ${outcome.failed
          .map((f) => `${joinDogNames(f.row.dogNames)}: ${f.reason}`)
          .join(' · ')}`
      : null;
  return { ok, problem };
}

/**
 * The banner copy after a household check-in, or null when every dog went
 * through. Names the dogs that did NOT check in and why, and — when some
 * did — names those too, so the desk knows exactly who is in:
 *   "Checked in Hazel & Hank. Not checked in — Skipper: already checked in"
 *
 * One truth with `describeHouseholdCheckIn`: this is that wording joined.
 */
export function summarizeHouseholdCheckIn(
  outcome: HouseholdCheckInOutcome<HouseholdRow>,
): string | null {
  const { ok, problem } = describeHouseholdCheckIn(outcome);
  if (problem === null) return null;
  return ok === null ? problem : `${ok} ${problem}`;
}
